/**
 * App root.
 *
 * Sets up routes and renders inside the AppShell. Uses Motion's
 * AnimatePresence so each route swap fades + slides instead of the
 * default instant cut. The LiquidFilters component renders once at
 * the top so all glass surfaces can reference the SVG distortion
 * filter via `filter: url(#liquid-distort)`.
 */
import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { AppShell } from '@/components/layout/AppShell';
import { PageTransition } from '@/components/layout/PageTransition';
import { LiquidFilters } from '@/components/glass';
import { Dashboard } from '@/pages/Dashboard';
import { Slots } from '@/pages/Slots';
import { SlotDetail } from '@/pages/SlotDetail';
import SlotsArchive from '@/pages/SlotsArchive';
import { Network } from '@/pages/Network';
import { NetworkTree } from '@/pages/NetworkTree';
import { Team } from '@/pages/Team';
import { QuickStart } from '@/pages/QuickStart';
import { Wallet } from '@/pages/Wallet';
import { Progress } from '@/pages/Progress';
import { useAuth } from '@/contexts/AuthContext';
import { OnboardingShell } from '@/components/onboarding/OnboardingShell';
import { CoachMarkTour } from '@/components/onboarding/CoachMarkTour';
import { WelcomeBonusToast } from '@/components/onboarding/WelcomeBonusToast';
import { OfertaShell } from '@/components/oferta/OfertaShell';
import { AccessGate } from '@/components/AccessGate';
import { useGlobalClickSound } from '@/hooks/useGlobalClickSound';
import { ShareCardLauncher } from '@/components/share/ShareCardLauncher';

export function App() {
  const location = useLocation();
  const { user, accessState, requestAccess, requestingAccess } = useAuth();
  // Глобальный аудио-фидбек: клики по ЛЮБЫМ <button>/interactive
  // элементам проигрывают uiTap. Специфические звуки (bellOpen,
  // claimStart и т.д.) играются поверх через свои handlers.
  useGlobalClickSound();

  // not_requested / pending / blocked — рендерим ТОЛЬКО заглушку,
  // приложение не открывается
  if (accessState) {
    return (
      <AccessGate
        state={accessState}
        onRequestAccess={() => void requestAccess()}
        requesting={requestingAccess}
      />
    );
  }

  // Show the user's real USDT balance once the JWT handshake
  // completes; fall back to 0 while loading so the pill never flashes
  // an old mock value.
  const balance = user?.usdtBalance ? Number(user.usdtBalance) : 0;

  return (
    <AppShell balance={balance}>
      {/* Global SVG filter defs — referenced from .lg-pill-blur and
          .lg-surface-blur via backdrop-filter url(#lg-glass-warp) */}
      <LiquidFilters />
      {/* Слушает ?share=<amount> в URL и показывает модалку с кнопкой
          «Поделиться в сторис» — для диплинков из push-бота про авто-сбор. */}
      <ShareCardLauncher />
      <AnimatePresence mode="wait" initial={false}>
        <Routes location={location} key={location.pathname}>
          <Route
            path="/"
            element={
              <PageTransition>
                <Dashboard />
              </PageTransition>
            }
          />
          <Route
            path="/slots"
            element={
              <PageTransition>
                <Slots />
              </PageTransition>
            }
          />
          <Route
            path="/slots/archive"
            element={
              <PageTransition>
                <SlotsArchive />
              </PageTransition>
            }
          />
          <Route
            path="/slots/:slotId"
            element={
              <PageTransition>
                <SlotDetail />
              </PageTransition>
            }
          />
          <Route
            path="/network"
            element={
              <PageTransition>
                <Network />
              </PageTransition>
            }
          />
          <Route
            path="/network/tree"
            element={
              <PageTransition>
                <NetworkTree />
              </PageTransition>
            }
          />
          <Route
            path="/network/team"
            element={
              <PageTransition>
                <Team />
              </PageTransition>
            }
          />
          <Route
            path="/quick"
            element={
              <PageTransition>
                <QuickStart />
              </PageTransition>
            }
          />
          <Route
            path="/wallet"
            element={
              <PageTransition>
                <Wallet />
              </PageTransition>
            }
          />
          <Route
            path="/progress"
            element={
              <PageTransition>
                <Progress />
              </PageTransition>
            }
          />
        </Routes>
      </AnimatePresence>
      <OnboardingShell />
      <OfertaShell />
      <CoachMarkTour />
      <WelcomeBonusToast />
    </AppShell>
  );
}
